// Estado de la sesión KYC durante el flujo completo
const KYC_STATUS = {
  IDLE: 'idle',
  CAPTURING_ID: 'capturing_id',
  VALIDATING_ID: 'validating_id',
  // Estado intermedio: el backend detectó una INE con solo el anverso.
  // El usuario debe subir el reverso para que el pipeline pueda continuar.
  CAPTURING_INE_BACK: 'capturing_ine_back',
  CAPTURING_POA: 'capturing_poa',
  VALIDATING_POA: 'validating_poa',
  CAPTURING_LIVENESS: 'capturing_liveness',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  CONDITIONAL: 'conditional',
  // Estado D-6: uno o más documentos requieren revisión humana asíncrona.
  // La sesión queda marcada en Firestore via POST /kyc/human-review/{session_id}.
  PENDING_HUMAN_REVIEW: 'pending_human_review',
  // D-7: estados de gestión de estado KYC por usuario
  // Consultando el estado KYC previo del usuario en kyc_users (Firestore).
  CHECKING_STATUS: 'checking_status',
  // El pipeline backend (extraction → validation → storage) está corriendo como gate de score.
  // El frontend muestra "validando tu documento" mientras el servidor procesa.
  AWAITING_EXTRACTION_SCORE: 'awaiting_extraction_score',
  // El pipeline backend está validando el comprobante de domicilio (extraction + freshness).
  // Intermedio entre VALIDATING_POA (score OK) y POA_VALIDATION_SUCCESS/FAILED.
  AWAITING_POA_PIPELINE: 'awaiting_poa_pipeline',
  // El usuario ya tiene un proceso KYC completado. Se muestran los resultados anteriores
  // y, si ALLOW_KYC_RESET=true, la opción de reiniciar.
  COMPLETED_PREVIOUS: 'completed_previous',
  // El proceso del usuario está en revisión humana — no puede iniciar uno nuevo.
  USER_IN_HUMAN_REVIEW: 'user_in_human_review',
  // D-8: proceso previo in_progress detectado — el frontend sondea getUserKycStatus
  // cada RESUME_POLL_INTERVAL_MS hasta que el pipeline server-side resuelve el estado.
  // El usuario puede abandonar la espera y comenzar una nueva captura.
  RESUMING_SESSION: 'resuming_session',
  // El usuario cerró el widget mientras un intento anterior terminó en rejected o error.
  // Se muestra una pantalla informativa antes de permitir un nuevo intento.
  PREVIOUS_FAILURE: 'previous_failure',
} as const;

type KYCStatus = (typeof KYC_STATUS)[keyof typeof KYC_STATUS];

// Tipos de documento aceptados para el proceso KYC de Amarello
const DOC_TYPE = {
  INE: 'ine',
  PASSPORT: 'passport',
  PROOF_OF_ADDRESS: 'proof_of_address',
} as const;

type DocType = (typeof DOC_TYPE)[keyof typeof DOC_TYPE];

// Resultado de scoring de un documento individual (0-100)
interface DocumentScore {
  score: number;
  passed: boolean;
  details: Record<string, unknown>;
}

// Datos de sesión KYC recibidos desde el Gateway de Parallelsoft
interface KYCSession {
  sessionId: string;
  token: string;
  playerName?: string;
  playerBirthDate?: string;
  playerCurp?: string;
  // Activado por el gateway según el feature flag liveness_enabled de Firestore
  livenessEnabled?: boolean;
  // D-7: identificador del usuario para gestión de estado persistente entre sesiones.
  // Si está presente, el flujo inicia en CHECKING_STATUS para verificar el estado previo.
  userId?: string;
}

// Resultado final del proceso KYC
interface KYCResult {
  status: KYCStatus;
  idDocType?: DocType;
  idScore?: DocumentScore;
  poaScore?: DocumentScore;
  livenessScore?: number;
  rejectionReason?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de Prueba de Vida (Liveness)
// ─────────────────────────────────────────────────────────────────────────────

// Estados de la sesión de liveness devueltos por el liveness-service
const LIVENESS_SESSION_STATUS = {
  CREATED: 'CREATED',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
} as const;

type LivenessSessionStatus = (typeof LIVENESS_SESSION_STATUS)[keyof typeof LIVENESS_SESSION_STATUS];

// Resultado de una sesión de liveness — retornado por GET /liveness/sessions/{id}/result
interface LivenessResult {
  sessionId: string;
  status: LivenessSessionStatus;
  // Confianza de Rekognition (0-100). Presente solo si status === SUCCEEDED
  confidence: number | null;
  // true si confidence >= 90% (umbral KYC financiero). Presente solo si status === SUCCEEDED
  isLive: boolean | null;
  // true/false si la comparación facial se ejecutó correctamente.
  // null si se omitió (doc_filename no enviado) o falló técnicamente (no bloquea aprobación).
  faceMatch: boolean | null;
  // Porcentaje de similitud facial (0-100) de Rekognition CompareFaces.
  // Presente solo cuando faceMatch no es null.
  faceMatchScore: number | null;
}

// Credenciales AWS temporales para que el SDK de Amplify acceda a Rekognition / KVS
// El liveness-service las vende via GET /api/v1/liveness/credentials
interface AwsLivenessCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  // Presente cuando son credenciales temporales de STS (sessionToken requerido con ellas)
  sessionToken?: string;
  expiration?: Date;
}

export { KYC_STATUS, DOC_TYPE, LIVENESS_SESSION_STATUS };
export type {
  KYCStatus,
  DocType,
  DocumentScore,
  KYCSession,
  KYCResult,
  LivenessSessionStatus,
  LivenessResult,
  AwsLivenessCredentials,
};
